import './FormBlock.scss'

import {
	Checkbox,
	CheckboxType,
	File,
	FileType,
	Input,
	InputType,
	Radio,
	RadioType,
	Select,
	SelectType
} from './form/fields'
import {FormButton, FormButtons, FormFields, FormGroup} from '../layout/Form'
import {FormProvider, useForm} from 'react-hook-form'
import {
	GoogleReCaptchaProvider,
	useGoogleReCaptcha
} from 'react-google-recaptcha-v3'
import React, {useRef, useState} from 'react'
import {Text, TextType} from './form'
import {useLanguage, useTranslation} from '../../util/i18n'

import {Block} from './Blocks'
import {FileError} from 'react-dropzone'
import {MarkedTitle} from '../ui/MarkedTitle'
import {OptionType} from '../ui/Dropdown'
import {Theme} from '../Theme'
import {scrollTo} from '../../util/scrollmemory'
import {style} from '../../util/style'

const styles = style('formblock')

type BaseFieldType = {
	id: number
	parent_id: number
	name: string
}

type ExtendedBaseFieldType = BaseFieldType & {
	label: string
	required: boolean
}

export type FormInputType = InputType &
	ExtendedBaseFieldType & {channel: 'input'}

export type FormFilesType = FileType &
	ExtendedBaseFieldType & {channel: 'files'}

export type FormSelectType = SelectType &
	ExtendedBaseFieldType & {channel: 'select'}

export type FormRadioType = RadioType &
	ExtendedBaseFieldType & {channel: 'radio'}

export type FormCheckboxType = CheckboxType &
	ExtendedBaseFieldType & {channel: 'checkbox'}

export type FormTextType = TextType & BaseFieldType & {channel: 'text'}

export type FormType =
	| FormInputType
	| FormFilesType
	| FormSelectType
	| FormRadioType
	| FormCheckboxType
	| FormTextType

export type FieldTypes = Array<FormType | any>

export type FormSectionType = {
	id: number
	parent_id: number
	channel: 'section'
	label: string
	fields: FieldTypes
}

export type FormStepType = {
	id: number
	parent_id: number
	channel: 'step'
	label: string
	submit_text: string
	sections: Array<FormSectionType>
}

export type FormDataType = {[key: string]: string | boolean | OptionType}

export type FormBlockType = {
	type: 'form'
	path: string
	reCaptchaKey: string
	id: number
	pageId: number
	steps: Array<FormStepType>
	requestUri?: string
	data?: {[key: string]: any}
	onChange?: (key: string, value: any, currentFormData: FormDataType) => void
	useEffect?: boolean
	onSubmit?: (response: {[key: string]: any}) => void
}

export const FormBlock: React.FC<
	FormBlockType & {
		container?: boolean
	}
> = ({
	id,
	pageId,
	path,
	reCaptchaKey,
	steps,
	container,
	data: defaultFormData = {},
	requestUri,
	onChange: onChangeHandler,
	onSubmit: onSubmitHandler,
	useEffect
}) => {
	const methods = useForm({
		shouldUnregister: false,
		defaultValues: defaultFormData
	})
	const element = useRef<HTMLDivElement>()
	const {setError, errors, getValues, setValue, watch} = methods
	const [currentStep, setCurrentStep] = useState<number>(0)
	const [thanksMessage, setThanksMessage] = useState<string>()
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
	const [files, setFiles] = useState<{[key: string]: File}>()
	const language = useLanguage()
	const t = useTranslation('form')

	const values = getValues()
	React.useEffect(() => {
		if (useEffect) {
			Object.keys(defaultFormData).forEach((key) => {
				setValue(key, defaultFormData[key])
			})
		}
	}, [defaultFormData])

	const handleSubmitReCaptchaVerify = async () => {
		const {executeRecaptcha} = useGoogleReCaptcha()
		if (!executeRecaptcha) {
			throw new Error('ReCaptcha')
		}
		return executeRecaptcha(path.replace(/-/g, '_'))
	}

	const handleSubmitErrors = async (response: Response): Promise<void> => {
		return response
			.json()
			.then((responseErrors: {[key: string]: Array<string> | string}) => {
				// collect the ids of the steps which occurs errors
				const stepsWithErrors: Array<number> = [
					...new Set(
						Object.keys(responseErrors).map((key: string) => {
							const keys: Array<string> = key.split('-', 3)
							return parseInt(keys[0], 10)
						})
					)
				]

				// calculate the index of the first step containing errors
				const firstStepsWithErrors: Array<FormStepType> = steps.filter((step) =>
					stepsWithErrors.includes(step.id)
				)
				const firstStepWithErrors: number = firstStepsWithErrors.length
					? firstStepsWithErrors[0].id
					: steps[0].id
				const firstStepWithErrorsIndex: number = steps.findIndex(
					(step) => step.id === firstStepWithErrors
				)

				// open the first step which occurs errors
				setCurrentStep(firstStepWithErrorsIndex)

				// collect errors
				type fileErrorType = {
					file: {name: string; size: number}
					errors: Array<FileError>
				}
				const fileErrors: {[key: string]: Array<fileErrorType>} = {}
				const fieldErrors: {[key: string]: Array<string>} = {}
				Object.keys(responseErrors).forEach((key: string) => {
					const cleanKey: string = key.split('.', 1)[0] // remove dot from the key (for file uploads)
					const keys = cleanKey.split('-', 3)
					const stepKey: number = parseInt(keys[0], 10)
					const sectionKey: number = parseInt(keys[1], 10)
					const fieldKey: number = parseInt(keys[2], 10)

					const tempStep: FormStepType = steps.find(
						(step) => step.id === stepKey
					)
					if (!tempStep) return
					const field = tempStep.sections
						.find((section) => section.id === sectionKey)
						.fields.find((f) => f.id === fieldKey)

					if (!field) return

					if (field.channel === 'files') {
						if (Array.isArray(responseErrors[key]))
							(responseErrors[key] as Array<string>).forEach(
								(message: string) => {
									const messageParts = message.split('||', 2)
									const fileName: string = messageParts[0]
									const cleanedMessage: string = `${fileName}${messageParts[1]}`

									if (!fileErrors[cleanKey]) fileErrors[cleanKey] = []

									const currentFileErrors: fileErrorType = fileErrors[
										cleanKey
									].find((fileError) => fileError.file.name === fileName)
									if (currentFileErrors) {
										currentFileErrors.errors.push({
											code: null,
											message: cleanedMessage
										})
									} else {
										fileErrors[cleanKey].push({
											file: {name: fileName, size: 0},
											errors: [{code: null, message: cleanedMessage}]
										})
									}
								}
							)
					} else {
						if (Array.isArray(responseErrors[key]))
							fieldErrors[key] = responseErrors[key] as Array<string>
					}

					// add the error to the corresponding field
					const allErrors = {...fileErrors, ...fieldErrors}
					Object.keys(allErrors).forEach((errorKey: string) => {
						setError(errorKey, {
							type: 'validate',
							message: JSON.stringify(allErrors[errorKey])
						})
					})
				})
			})
	}

	const onSubmit = (data) => {
		if (currentStep !== steps.length - 1) {
			setCurrentStep(currentStep + 1)
			scrollTo(() => 35, element.current)
		} else sendFormData(data)
	}

	const sendFormData = (data) => {
		if (isSubmitting) return null
		setIsSubmitting(true)
		handleSubmitReCaptchaVerify()
			.then((token: string) => {
				const formBodyData = new FormData()
				// transform form data to send it to the server
				Object.keys(data).forEach((key) => {
					if (typeof data[key] === 'object') {
						const keys: Array<string> = key.split('-', 3)
						const step = steps.find(
							(stepItem) => stepItem.id === parseInt(keys[0], 10)
						)
						const section = step
							? step.sections.find(
									(sectionItem) => sectionItem.id === parseInt(keys[1], 10)
							  )
							: null
						const objectField = section
							? section.fields.find(
									(field) => field.id === parseInt(keys[2], 10)
							  )
							: null

						if (objectField && objectField.channel === 'select') {
							// only used when the select field was not selected and has its default object value instead of the string
							formBodyData.append(`${key}`, (data[key] as OptionType).path)
						} else if (objectField && objectField.channel === 'files') {
							Object.keys(data[key]).forEach((fileKey) => {
								if (files[key] && files[key][fileKey]) {
									formBodyData.append(`${key}[]`, files[key][fileKey])
								}
							})
						}
					} else if (typeof data[key] === 'boolean') {
						formBodyData.append(key, data[key] ? 'true' : 'false')
					} else formBodyData.append(key, data[key] as string)
				})

				// send the form data to the server
				fetch(
					requestUri ? requestUri : `/api/form/${id}/nl/submit/${pageId}`, // Force 'nl', because the language in the url is changed by GTranslate
					{
						method: 'POST',
						headers: {
							ReCaptchaToken: token
						},
						body: formBodyData
					}
				).then((response) => {
					setIsSubmitting(false)
					if (response.ok) {
						// the form was submitted succesfully
						return response.json().then((responseData) => {
							if (onSubmitHandler) {
								onSubmitHandler(responseData)
							} else {
								// set the thanks message
								setThanksMessage(responseData.thanks)
								scrollTo(() => 35, element.current)
							}
						})
					}
					// The form had errors
					// handle the errors
					return handleSubmitErrors(response)
				})
			})
			.catch((reason) => {
				console.error('ReCaptcha', reason)
				setIsSubmitting(false)
			})
	}

	const getFieldComponents = (
		fields: FieldTypes,
		stepId: number,
		sectionId: number
	) =>
		fields.map((field) => {
			if (!field) return null
			const name = `${stepId}-${sectionId}-${field.id}`
			switch (field.channel) {
				case 'checkbox':
					return (
						<Checkbox
							{...field}
							name={name}
							key={field.id}
							mod={['maxwidth', errors[name] ? 'error' : null]}
							checked={!!values[name]}
							onChange={(value: boolean) => {
								if (onChangeHandler) {
									const newFormData = {
										...getValues(),
										[name]: value
									} as FormDataType
									onChangeHandler(name, value, newFormData)
								}
								setValue(name, value)
							}}
						/>
					)
				case 'files':
					return (
						<File
							{...field}
							name={name}
							key={field.id}
							mod={[
								field.mod as 'half' | 'maxwidth',
								errors[name] ? 'error' : null
							]}
							value={values[name]}
							serverErrors={[errors[name]?.message].filter(
								(m) => m !== undefined
							)}
							onChange={(value) => {
								if (onChangeHandler) {
									const newFormData = {
										...getValues(),
										[name]: value
									} as FormDataType
									onChangeHandler(name, value, newFormData)
								}
								setValue(name, value)
								setFiles({...files, [name]: value})
							}}
						/>
					)
				case 'input':
					const newName = field.confirmed ? `${name}_confirmation` : name
					return (
						<Input
							{...field}
							name={newName}
							key={field.confirmed ? `${field.id}_confirmation` : field.id}
							mod={[
								field.mod as 'half' | 'maxwidth',
								errors[newName] ? 'error' : null
							]}
							value={values[newName] as string}
							onChange={(value: string) => {
								if (onChangeHandler) {
									const newFormData = {
										...getValues(),
										[newName]: value
									} as FormDataType
									onChangeHandler(newName, value, newFormData)
								}
								setValue(newName, value)
							}}
						/>
					)
				case 'radio':
					return (
						<Radio
							{...field}
							name={name}
							key={field.id}
							mod={errors[name] ? 'error' : null}
							selectedOption={values[name] as string}
							onChange={(value: string) => {
								if (onChangeHandler) {
									const newFormData = {
										...getValues(),
										[name]: value
									} as FormDataType
									onChangeHandler(name, value, newFormData)
								}
								setValue(name, value)
							}}
						/>
					)
				case 'select':
					const selected = watch(name)

					if (!selected && field.options.length > 0) {
						setValue(name, field.options[0].path)
					}

					return (
						<Select
							{...field}
							name={name}
							key={field.id}
							mod={[
								field.mod as 'half' | 'maxwidth',
								errors[name] ? 'error' : null
							]}
							selectedOption={field.options.find(
								(option) => option.path === selected
							)}
							enableFilter={field.enableFilter}
							onChange={(value: string) => {
								if (onChangeHandler) {
									const newFormData = {
										...getValues(),
										[name]: value
									} as FormDataType
									onChangeHandler(name, value, newFormData)
								}
								setValue(name, value)
							}}
						/>
					)
				case 'text':
					return <Text {...field} key={field.id} />
				default:
					return field || null
			}
		})

	return (
		<Block className={styles()} container={container}>
			<div className={styles('container')} ref={element}>
				{thanksMessage && (
					<div className={styles('container-thanks')}>
						<Theme.Wysiwyg>{thanksMessage}</Theme.Wysiwyg>
					</div>
				)}
				{!thanksMessage && (
					<GoogleReCaptchaProvider reCaptchaKey={reCaptchaKey}>
						<FormProvider {...methods}>
							<form
								id={path}
								onSubmit={(e) => {
									e.preventDefault()
									onSubmit(getValues())
									return false
								}}
							>
								{steps &&
									steps
										.filter((step, index) => index === currentStep)
										.map((step) => (
											<div className={styles('container-step')} key={step.id}>
												{step.sections.map((section, sectionIndex) => (
													<FormGroup
														key={section.id}
														title={
															section.label !== null && section.label !== ''
														}
														button={sectionIndex + 1 === step.sections.length}
													>
														<MarkedTitle
															title={section.label}
															direction="left"
														/>
														<FormFields>
															{getFieldComponents(
																section.fields,
																step.id,
																section.id
															)}
														</FormFields>
														{sectionIndex + 1 === step.sections.length && (
															<FormButtons overflow={true}>
																{currentStep !== 0 && (
																	<FormButton
																		className={styles('container-step-back')}
																	>
																		<Theme.Button
																			disabled={isSubmitting}
																			mod={'greybg'}
																			onClick={(e) => {
																				if (!isSubmitting)
																					setCurrentStep(currentStep - 1)
																			}}
																		>
																			{t.back}
																		</Theme.Button>
																	</FormButton>
																)}
																<FormButton>
																	<Theme.Button
																		as="button"
																		disabled={isSubmitting}
																	>
																		{step.submit_text || t.submit}
																	</Theme.Button>
																</FormButton>
															</FormButtons>
														)}
													</FormGroup>
												))}
											</div>
										))}
							</form>
						</FormProvider>
					</GoogleReCaptchaProvider>
				)}
			</div>
		</Block>
	)
}
